import { useEffect, useState, useCallback } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import {
  Activity,
  AlertCircle,
  ArrowRightLeft,
  Clock,
  KanbanSquare,
  Loader,
  MessageSquare,
  RefreshCcw,
} from 'lucide-react';

import { incidentsApiService } from '../../services/incidentsApi';
import { CreatorProfile } from '../CreatorProfile';
import { MarkdownRenderer } from '../MarkdownRenderer';
import { SectionHeader } from '../ui/SectionHeader';
import { SkeletonLoader } from '../SkeletonLoader';
import { cn } from '../../utils/cn';
import type {
  IncidentTimelineEntry,
  IncidentTimelineSource,
} from '../../types/incident';

export interface IncidentTimelineSectionProps {
  incidentId: string;
  /**
   * Bumped by the parent on any incident WS event so the timeline auto-refreshes.
   * (We can't fetch only deltas because the `since` query param is forward-only and
   * we want full ordering on the client; a simple refetch is fine.)
   */
  refreshNonce?: number;
  /** Default 200; max 500 per backend. */
  limit?: number;
}

const SOURCE_META: Record<
  IncidentTimelineSource,
  { label: string; color: string; icon: typeof Activity }
> = {
  incident_comment: { label: 'Comment', color: 'text-blue-300', icon: MessageSquare },
  incident_activity: { label: 'Activity', color: 'text-indigo-300', icon: Activity },
  kanban_activity: { label: 'Kanban activity', color: 'text-accent-300', icon: KanbanSquare },
  kanban_comment: { label: 'Kanban comment', color: 'text-accent-200', icon: MessageSquare },
  kanban_card_history: { label: 'Card moved', color: 'text-teal-300', icon: ArrowRightLeft },
};

/** Best-effort comment body extraction across the union of `payload` shapes. */
function extractCommentBody(entry: IncidentTimelineEntry): string | null {
  const p = entry.payload;
  if (!p || typeof p !== 'object') return null;
  const obj = p as Record<string, unknown>;
  if (typeof obj.content === 'string') return obj.content;
  if (typeof obj.body === 'string') return obj.body;
  if (typeof obj.message === 'string') return obj.message;
  return null;
}

export const IncidentTimelineSection = ({
  incidentId,
  refreshNonce = 0,
  limit = 200,
}: IncidentTimelineSectionProps) => {
  const [entries, setEntries] = useState<IncidentTimelineEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (initial: boolean) => {
      if (!incidentId) return;
      if (initial) setLoading(true);
      else setRefreshing(true);
      setError(null);
      try {
        const list = await incidentsApiService.getIncidentTimeline(incidentId, { limit });
        // Defensive: server says newest-first but normalise just in case.
        const sorted = [...list].sort((a, b) => b.at - a.at);
        setEntries(sorted);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load timeline');
      } finally {
        if (initial) setLoading(false);
        setRefreshing(false);
      }
    },
    [incidentId, limit],
  );

  useEffect(() => {
    void load(true);
  }, [load]);

  useEffect(() => {
    if (refreshNonce === 0) return;
    void load(false);
  }, [refreshNonce, load]);

  const count = entries?.length ?? 0;
  const cappedHint = count >= limit;

  return (
    <div className="glass-card p-4 md:p-5">
      <SectionHeader
        as="h2"
        title={
          <span className="inline-flex items-center gap-2">
            Timeline
            {refreshing && <Loader className="w-3.5 h-3.5 animate-spin text-glass-400" />}
          </span>
        }
        icon={Clock}
        count={count}
        description="Merged stream of incident comments, incident activity, kanban activity on linked cards, kanban comments, and card list-move history. Sorted newest first."
        actions={
          <button
            type="button"
            onClick={() => void load(false)}
            disabled={loading || refreshing}
            className="btn-icon"
            title="Refresh"
            aria-label="Refresh timeline"
          >
            <RefreshCcw className="w-4 h-4" />
          </button>
        }
      />

      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          <AlertCircle />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="space-y-2" aria-busy="true">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="subcard p-3 flex items-start gap-3">
              <SkeletonLoader className="w-7 h-7 rounded-md" />
              <div className="flex-1 space-y-2">
                <SkeletonLoader className="h-3 w-40 max-w-full" />
                <SkeletonLoader className="h-4 w-full max-w-lg" />
              </div>
            </div>
          ))}
        </div>
      ) : count === 0 ? (
        <p className="text-sm text-glass-400">No timeline entries yet.</p>
      ) : (
        <div className="space-y-2">
          {(entries ?? []).map((entry) => {
            const meta = SOURCE_META[entry.source] ?? {
              label: entry.source,
              color: 'text-glass-300',
              icon: Activity,
            };
            const Icon = meta.icon;
            const isComment =
              entry.source === 'incident_comment' || entry.source === 'kanban_comment';
            const commentBody = isComment ? extractCommentBody(entry) : null;

            return (
              <div
                key={`${entry.source}-${entry.id}`}
                className="subcard p-3"
              >
                <div className="flex items-start gap-3">
                  <div
                    className={cn('icon-tile icon-tile-sm', meta.color)}
                    title={meta.label}
                  >
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap text-xs">
                      <span className={cn('font-semibold', meta.color)}>{meta.label}</span>
                      <span className="text-glass-500">·</span>
                      <CreatorProfile creatorId={entry.actor_id} inline />
                      <span className="text-glass-500">·</span>
                      <span
                        className="text-glass-400 tabular-nums"
                        title={format(new Date(entry.at), 'PPpp')}
                      >
                        {formatDistanceToNow(new Date(entry.at), { addSuffix: true })}
                      </span>
                    </div>
                    <p className="text-sm text-glass-200 mt-1 break-words">{entry.summary}</p>
                    {commentBody && commentBody !== entry.summary && (
                      <div className="mt-2 text-sm">
                        <MarkdownRenderer content={commentBody} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {cappedHint && (
        <p className="text-xs text-glass-500 mt-3">
          Showing the most recent {limit} entries. Older history is still available via the
          incident&apos;s activity endpoint.
        </p>
      )}
    </div>
  );
};
